import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { SignedIn, SignedOut, useUser } from "@clerk/clerk-react";
import { Plus, Users, Euro, Calendar, X, Lock } from "lucide-react";
import { toast } from "sonner";
import { useApi, formatDate, formatEuro, CATEGORY_LABELS } from "@/lib/api";
import { GoldSignIn } from "@/components/Nav";

const CATS = [["table", "La Table"], ["salon", "Le Salon"], ["atelier", "L'Atelier"]];
const emptyForm = {
  title: "", venue_name: "", venue_type: "", city: "Paris", category: "table",
  date: "", price: 60, seats_total: 12, description: "", story: "",
  included: "", image: "https://images.unsplash.com/photo-1788093153458-5688bac45381?crop=entropy&cs=srgb&fm=jpg&q=85",
};

function CreateForm({ onClose }) {
  const api = useApi();
  const qc = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/evenings", {
        ...form,
        price: parseFloat(form.price),
        seats_total: parseInt(form.seats_total, 10),
        date: new Date(form.date).toISOString(),
        included: form.included.split("\n").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Evening created");
      qc.invalidateQueries({ queryKey: ["host-evenings"] });
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create evening");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-velvet/70 backdrop-blur-sm" data-testid="create-evening-modal">
      <div className="bg-parchment-light max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-stone-border">
        <div className="flex items-center justify-between p-6 border-b border-stone-border sticky top-0 bg-parchment-light">
          <h3 className="font-serif text-2xl text-wine">Create an evening</h3>
          <button onClick={onClose} data-testid="close-create-modal"><X className="w-5 h-5 text-stone hover:text-wine" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4" data-testid="create-evening-form">
          <input required className="field" placeholder="Title — Un dîner aux chandelles" value={form.title} onChange={set("title")} data-testid="ev-title" />
          <div className="grid sm:grid-cols-2 gap-4">
            <input required className="field" placeholder="Venue name" value={form.venue_name} onChange={set("venue_name")} data-testid="ev-venue" />
            <input required className="field" placeholder="Venue type (château…)" value={form.venue_type} onChange={set("venue_type")} data-testid="ev-type" />
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <input required className="field" placeholder="City" value={form.city} onChange={set("city")} data-testid="ev-city" />
            <select className="field" value={form.category} onChange={set("category")} data-testid="ev-category">
              {CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <input required type="datetime-local" className="field" value={form.date} onChange={set("date")} data-testid="ev-date" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input required type="number" min="1" className="field" placeholder="Price € / seat" value={form.price} onChange={set("price")} data-testid="ev-price" />
            <input required type="number" min="1" className="field" placeholder="Total seats" value={form.seats_total} onChange={set("seats_total")} data-testid="ev-seats" />
          </div>
          <input className="field" placeholder="Image URL" value={form.image} onChange={set("image")} data-testid="ev-image" />
          <textarea required rows={2} className="field resize-none" placeholder="Short description" value={form.description} onChange={set("description")} data-testid="ev-desc" />
          <textarea rows={3} className="field resize-none" placeholder="The story of the house" value={form.story} onChange={set("story")} data-testid="ev-story" />
          <textarea rows={3} className="field resize-none" placeholder="What's included (one per line)" value={form.included} onChange={set("included")} data-testid="ev-included" />
          <button type="submit" disabled={busy} className="btn-gold w-full py-3.5 text-sm font-semibold tracking-wide disabled:opacity-60" data-testid="submit-evening-btn">
            {busy ? "Creating…" : "Publish evening"}
          </button>
        </form>
      </div>
      <style>{`.field{width:100%;background:#fff;border:1px solid var(--stone-border);padding:0.6rem 0.8rem;font-size:0.9rem;color:var(--ink);outline:none}.field:focus{border-color:var(--gold)}`}</style>
    </div>
  );
}

function Dashboard() {
  const api = useApi();
  const [creating, setCreating] = useState(false);
  const [openEvening, setOpenEvening] = useState(null);

  const { data: evenings = [], isLoading } = useQuery({
    queryKey: ["host-evenings"],
    queryFn: async () => (await api.get("/host/evenings")).data,
  });

  const { data: guests = [] } = useQuery({
    queryKey: ["host-bookings", openEvening],
    enabled: !!openEvening,
    queryFn: async () => (await api.get(`/host/evenings/${openEvening}/bookings`)).data,
  });

  const totalRevenue = evenings.reduce((s, e) => s + (e.revenue || 0), 0);
  const totalSeats = evenings.reduce((s, e) => s + (e.seats_booked || 0), 0);

  return (
    <div data-testid="host-dashboard-inner">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5 mb-10">
        <div>
          <div className="eyebrow mb-3">Host</div>
          <h1 className="font-serif text-4xl lg:text-5xl text-wine">Your evenings</h1>
        </div>
        <button onClick={() => setCreating(true)} data-testid="new-evening-btn" className="btn-gold px-6 py-3 text-sm font-semibold tracking-wide flex items-center gap-2 self-start">
          <Plus className="w-4 h-4" /> New evening
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-10">
        <Stat icon={Calendar} label="Evenings" value={evenings.length} />
        <Stat icon={Users} label="Seats sold" value={totalSeats} />
        <Stat icon={Euro} label="Revenue" value={formatEuro(totalRevenue)} />
      </div>

      {isLoading ? (
        <div className="text-center py-20 text-stone font-serif text-2xl">Loading…</div>
      ) : evenings.length === 0 ? (
        <div className="card-parchment p-12 text-center">
          <p className="font-serif text-2xl text-wine mb-2">No evenings yet.</p>
          <p className="text-ink-muted">Create your first evening to start welcoming guests.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="host-evenings-list">
          {evenings.map((e) => (
            <div key={e.id} className="card-parchment flex flex-col sm:flex-row overflow-hidden" data-testid={`host-evening-${e.id}`}>
              <img src={e.image} alt="" className="sm:w-44 h-36 sm:h-auto object-cover img-duotone" />
              <div className="p-5 flex-1 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                <div>
                  <span className="eyebrow">{CATEGORY_LABELS[e.category]} · {formatDate(e.date)}</span>
                  <h3 className="font-serif text-2xl text-ink mt-1">{e.title}</h3>
                  <p className="text-sm text-ink-muted">{e.venue_name}</p>
                  <p className="text-xs text-stone mt-2">{e.seats_booked}/{e.seats_total} seats · {formatEuro(e.revenue)} earned</p>
                </div>
                <button onClick={() => setOpenEvening(e.id)} data-testid={`view-guests-${e.id}`} className="text-sm text-wine border border-stone-border px-5 py-2.5 hover:border-gold transition-colors whitespace-nowrap">
                  View guests
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && <CreateForm onClose={() => setCreating(false)} />}

      {openEvening && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-velvet/70 backdrop-blur-sm" data-testid="guests-modal">
          <div className="bg-parchment-light max-w-lg w-full max-h-[80vh] overflow-y-auto border border-stone-border">
            <div className="flex items-center justify-between p-6 border-b border-stone-border">
              <h3 className="font-serif text-2xl text-wine">Guest list</h3>
              <button onClick={() => setOpenEvening(null)} data-testid="close-guests-modal"><X className="w-5 h-5 text-stone hover:text-wine" /></button>
            </div>
            <div className="p-6">
              {guests.length === 0 ? (
                <p className="text-ink-muted text-center py-8">No bookings yet.</p>
              ) : (
                <ul className="space-y-3" data-testid="guest-list">
                  {guests.map((g) => (
                    <li key={g.id} className="flex items-center justify-between border-b border-stone-border/60 pb-3">
                      <div>
                        <p className="text-ink font-medium">{g.user_name || "Guest"}</p>
                        <p className="text-xs text-stone">{g.user_email}</p>
                      </div>
                      <span className="text-sm text-wine">{g.seats} seat{g.seats > 1 ? "s" : ""}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="card-parchment p-5 flex items-center gap-4">
      <div className="w-11 h-11 bg-wine/10 flex items-center justify-center"><Icon className="w-5 h-5 text-wine" /></div>
      <div>
        <div className="text-xs uppercase tracking-wide text-stone">{label}</div>
        <div className="font-serif text-2xl text-ink">{value}</div>
      </div>
    </div>
  );
}

export default function HostDashboard() {
  const { isLoaded } = useUser();
  return (
    <div data-testid="host-dashboard-page" className="pt-32 pb-24 min-h-[80vh]">
      <div className="max-w-5xl mx-auto px-4 sm:px-8">
        {isLoaded && (
          <>
            <SignedIn><Dashboard /></SignedIn>
            <SignedOut>
              <div className="text-center max-w-md mx-auto card-parchment p-12">
                <Lock className="w-10 h-10 text-gold mx-auto" />
                <h2 className="font-serif text-3xl text-wine mt-6">Host dashboard</h2>
                <p className="text-ink-muted mt-2 mb-8">Sign in to create and manage your evenings.</p>
                <GoldSignIn><button className="btn-gold px-8 py-3 text-sm font-semibold tracking-wide" data-testid="host-gate-signin">Sign in</button></GoldSignIn>
              </div>
            </SignedOut>
          </>
        )}
      </div>
    </div>
  );
}
